import { notifications } from '@mantine/notifications';
import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { create } from 'zustand';
import {
  type CommonQueryParams,
  type File,
  type Form,
  type FormRecipientsQueryParams,
  type FormsQueryParams,
  type FormsRecipientsResponse,
  type FormsResponse,
  LOGIN_METHOD,
  type LoginChallengeUserResponse,
  type Message,
  type MessageQueryParams,
  RustyAuth,
  RustyFormsApi,
  type Session,
  type StateBaseWithSubscription,
  type VerifiedEmail,
  type VerifiedEmailsResponse,
  type Verify2FALoginRequest,
} from '@/api';
import { createErrorHandler, createSuccessHandler } from '@/api/utils/notification-handlers';
import { API_BASE_URL, LOCAL_STORAGE_KEY } from './constants';
import { clearDecryptedMessagesCache } from './lib/nostr-decrypt';

const errorHandler = createErrorHandler({
  notifications,
  onError: (error: AxiosError) => {
    Sentry.captureException(error, {
      extra: {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
      },
    });

    console.log('API Error:', error);
  },
});

const successHandler = createSuccessHandler({
  notifications,
});

// Wired into `RustyAuth` via `onSessionChange` so any session mutation
// (login, refresh, whoami, logout) updates Sentry without each call site
// having to remember.
const updateSentryUserContext = (session: Session | null | undefined) => {
  if (session?.isLoggedIn) {
    Sentry.setUser({
      id: session.userId,
      email: session.email,
      username:
        session.displayName ??
        (session.publicKey
          ? `${session.publicKey.slice(0, 8)}...${session.publicKey.slice(-8)}`
          : session.userId),
    });

    Sentry.setTag('login_method', session.method || 'unknown');
    Sentry.setTag('user_authenticated', 'true');
  } else {
    Sentry.setUser(null);
    Sentry.setTag('user_authenticated', 'false');
  }
};

export interface VerifiedEmailsByForm {
  [formId: string]: VerifiedEmail[];
}

interface State extends StateBaseWithSubscription<RustyFormsApi> {
  forms: Form[];
  // Basically form recipients
  verifiedEmailsByForm: VerifiedEmailsByForm;

  getAndSetForms: (params?: FormsQueryParams) => Promise<FormsResponse>;
  getAndSetVerifiedEmails: () => Promise<VerifiedEmailsResponse>;
  getAndSetFormVerifiedEmails: (
    formId: string,
    params?: FormRecipientsQueryParams
  ) => Promise<VerifiedEmailsResponse>;
  getMessagesWithForms: (params: MessageQueryParams) => Promise<{
    data: {
      form: Form | undefined;
      files: File[];
      msg: Message;
    }[];
    total: number;
  }>;
  downloadFile: (id: string) => Promise<string>;
}

const api = new RustyFormsApi({
  baseUrl: API_BASE_URL,
  auth: new RustyAuth({
    baseUrl: API_BASE_URL,
    useLocalStore: true,
    localStorageKey: LOCAL_STORAGE_KEY,
    onSessionChange: updateSentryUserContext,
  }),
  errorHandler,
  successHandler,
});

export const useRustyState = create<State>((set, get) => ({
  init: () => {
    const session = get().getSession();
    updateSentryUserContext(session);
  },
  session: {
    isLoggedIn: false,
  },
  getSession: (): Session | undefined => {
    if (api.auth) {
      return api.auth.getSession();
    }
    console.warn('No auth instance available to get session');
  },
  login: async (identifier: string, loginMethod: LOGIN_METHOD) => {
    if (!api.auth) {
      throw new Error('No auth');
    }

    switch (loginMethod) {
      case LOGIN_METHOD.NOSTR:
        return await api.auth.login({
          type: LOGIN_METHOD.NOSTR,
          content: {
            public_key: identifier,
          },
        });
      case LOGIN_METHOD.EMAIL_MAGIC_LINK:
        return await api.auth.login({
          type: LOGIN_METHOD.EMAIL_MAGIC_LINK,
          content: {
            email: identifier,
          },
        });
      default:
        throw new Error(`Unsupported login method: ${loginMethod}`);
    }
  },
  loginChallenge: async (loginResponse: LoginChallengeUserResponse) => {
    if (!api.auth) {
      throw new Error('No auth');
    }

    if (loginResponse.type === LOGIN_METHOD.EMAIL_MAGIC_LINK) {
      return await api.auth.loginChallenge({
        type: LOGIN_METHOD.EMAIL_MAGIC_LINK,
        content: {
          id: loginResponse.content.id,
          challenge: loginResponse.content.challenge,
        },
      });
    } else if (loginResponse.type === LOGIN_METHOD.NOSTR) {
      return await api.auth.loginChallenge(loginResponse);
    }
    throw new Error('Unsupported challenge response');
  },
  verify2FA: async (request: Verify2FALoginRequest) => {
    if (!api.auth) {
      throw new Error('No auth');
    }
    return await api.auth.verify2FA(request);
  },
  logout: async () => {
    // Clear decrypted messages cache on logout. Sentry context clears
    // automatically via the `onSessionChange` hook registered on RustyAuth.
    // Runs first: once logout hands the browser to the IdP, the page unloads.
    await clearDecryptedMessagesCache();

    if (api.auth) {
      return api.auth.logout();
    }
    console.warn('No auth instance available to logout');
    return false;
  },
  api,
  verifiedEmails: [],
  forms: [],
  verifiedEmailsByForm: {},

  subscriptionPlans: [],
  currentSubscription: null,
  subscriptionUsage: null,

  referralCode: null,
  referralStats: null,
  getAndSetForms: async (params?: FormsQueryParams) => {
    const res = await api.getForms(params);
    if (res.data) {
      set({ forms: res.data });
      return res;
    }
    return {
      data: [],
      total: 0,
    };
  },
  getForm: async (id: string) => {
    const form = await api.getForm(id);
    if (!form) {
      throw new Error(`Could not get form with id: ${id}`);
    }
    return form;
  },
  getAndSetVerifiedEmails: async (): Promise<FormsRecipientsResponse> => {
    const res = await api.getVerifiedEmails();
    if (res.data) {
      set({ verifiedEmails: res.data });
      return res;
    }
    return {
      data: [],
      total: 0,
    };
  },
  getAndSetFormVerifiedEmails: async (
    formId: string,
    params?: FormRecipientsQueryParams
  ): Promise<VerifiedEmailsResponse> => {
    const res = await api.getFormRecipients(formId, params);
    if (res.data) {
      set({
        verifiedEmailsByForm: {
          ...get().verifiedEmailsByForm,
          [formId]: res.data,
        },
      });

      return res;
    }
    return {
      data: [],
      total: 0,
    };
  },
  getFormRecipients: async (formId: string, query: FormRecipientsQueryParams) => {
    const res = await api.getFormRecipients(formId, query);
    if (res.data) {
      set({
        verifiedEmailsByForm: {
          ...get().verifiedEmailsByForm,
          [formId]: res.data,
        },
      });

      return res.data;
    }
    return {
      data: [],
      total: 0,
    };
  },
  getMessagesWithForms: async (query: MessageQueryParams) => {
    const res = await api.getMessages(query);
    if (res) {
      const files = Array.isArray(res.files) ? res.files : [];

      return {
        data: res.data.map((m) => {
          const filteredFiles = files.filter(
            (f): f is File => 'message_id' in f && f.message_id === m.id
          );

          return {
            msg: m,
            files: filteredFiles,
            form: get().forms.find((f) => f.id === m.form_id),
          };
        }),
        total: res.total,
      };
    }
    return {
      data: [],
      total: 0,
    };
  },
  downloadFile: async (id: string) => {
    return api.downloadFile(id);
  },

  getAndSetSubscriptionPlans: async () => {
    const subscriptionPlans = await api.getSubscriptionPlans();
    set({ subscriptionPlans });
    return subscriptionPlans;
  },

  getAndSetCurrentSubscription: async () => {
    if (!api.auth?.hasValidAccessToken()) {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const result = await get().getAndSetCurrentSubscription();
          resolve(result);
        }, 500);
      });
    }
    const subscription = await api.getCurrentSubscription();
    set({ currentSubscription: subscription });
    return subscription;
  },

  getAndSetSubscriptionUsage: async () => {
    if (!api.auth?.hasValidAccessToken()) {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const result = await get().getAndSetSubscriptionUsage();
          resolve(result);
        }, 500);
      });
    }
    try {
      const usage = await api.getSubscriptionUsage();
      set({ subscriptionUsage: usage });
      return usage;
    } catch (error: any) {
      // If user doesn't have an active subscription, this will fail
      // Reset usage to null to indicate no subscription usage available
      set({ subscriptionUsage: null });
      console.warn('No subscription usage available:', error.message);
      return null;
    }
  },

  subscribeToplan: async (planId: string) => {
    try {
      const result = await api.subscribeToplan(planId);

      if (result.type === 'Checkout') {
        window.location.href = result.content.redirect_url;
        return result;
      }

      await get().getAndSetCurrentSubscription();
      return result;
    } catch (error: any) {
      if (
        error.response?.status === 400 &&
        error.response?.data?.error === 'CustomerPortalRequired' &&
        error.response?.data?.action === 'redirect_to_customer_portal'
      ) {
        const portalSession = await get().createCustomerPortalSession();
        window.location.href = portalSession.portal_url;
        return;
      }

      throw error;
    }
  },

  cancelSubscription: async () => {
    const result = await api.cancelSubscription();
    await get().getAndSetCurrentSubscription();
    return result;
  },

  createCustomerPortalSession: async () => {
    return await api.createCustomerPortalSession();
  },

  getAccountMovements: async (query: CommonQueryParams = {}) => {
    return await api.getAccountMovements(query);
  },

  getAndSetReferralCode: async () => {
    if (!api.auth?.hasValidAccessToken()) {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const result = await get().getAndSetReferralCode();
          resolve(result);
        }, 500);
      });
    }
    const code = await api.getReferralCode();
    set({ referralCode: code });
    return code;
  },

  getAndSetReferralStats: async () => {
    if (!api.auth?.hasValidAccessToken()) {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const result = await get().getAndSetReferralStats();
          resolve(result);
        }, 500);
      });
    }
    const stats = await api.getReferralStats();
    set({ referralStats: stats });
    return stats;
  },

  getReferralHistory: async () => {
    return await api.getReferralHistory();
  },
}));
